import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { createApiUrl } from '../utils/apiConfig';
import StandardPagination from './StandardPagination';
import './AllAssets.css';

const AllAssets = ({ assets, refetchData }) => {
    const navigate = useNavigate();
    const [deleteModal, setDeleteModal] = useState({ show: false, assetId: null, assetName: '' });
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(12);

    const handleDelete = (assetId, assetName) => {
        setDeleteModal({ show: true, assetId, assetName });
    };

    const handleEdit = (assetId) => {
        navigate(`/edit-asset/${assetId}`);
    };

    // Search and filter logic
    const filteredAssets = assets.filter(asset => 
        asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        asset._id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Pagination logic
    const totalPages = Math.ceil(filteredAssets.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentAssets = filteredAssets.slice(startIndex, endIndex);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1); // Reset to first page when searching
    };

    const confirmDelete = async () => {
        try {
            setLoading(true);
            await axios.delete(createApiUrl(`/api/asset/${deleteModal.assetId}`), {
                withCredentials: true
            });
            
            if (refetchData && typeof refetchData === 'function') {
                refetchData();
            }
            
            setDeleteModal({ show: false, assetId: null, assetName: '' });
        } catch (error) {
            // console.error('Error deleting asset:', error);
            alert('Failed to delete asset. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const cancelDelete = () => {
        setDeleteModal({ show: false, assetId: null, assetName: '' });
    };

    return (
        <div className="all-assets-container">
            <h2>All Assets</h2>
            
            {/* Search Bar - At the Top */}
            <div className="d-flex justify-content-end mb-3">
                <form onSubmit={handleSearch} className="form-inline d-flex">
                    <input
                        className="form-control mr-2"
                        type="search"
                        name="search"
                        placeholder="Search assets..."
                        aria-label="Search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoComplete="off"
                        style={{ 
                            width: '300px',
                            height: '38px',
                            fontSize: '14px'
                        }}
                    />
                    <button 
                        className="btn" 
                        type="submit"
                        style={{ 
                            height: '38px',
                            fontSize: '14px',
                            padding: '6px 12px',
                            backgroundColor: 'var(--darkPurple)',
                            color: 'var(--darkOrange)',
                            border: '1px solid var(--darkPurple)',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'var(--darkOrange)';
                            e.target.style.color = 'var(--darkPurple)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'var(--darkPurple)';
                            e.target.style.color = 'var(--darkOrange)';
                        }}
                    >
                        Search
                    </button>
                </form>
            </div>
            
            {/* Items Per Page Controls - Below Search */}
            <div className="row mb-3">
                <div className="col-md-6">
                    <div className="d-flex align-items-center">
                        <label htmlFor="assetsPerPage" className="form-label me-2 mb-0">Assets per page:</label>
                        <select 
                            id="assetsPerPage"
                            className="form-select"
                            value={itemsPerPage}
                            onChange={(e) => setItemsPerPage(parseInt(e.target.value))}
                            style={{ 
                                maxWidth: '80px', 
                                height: '32px',
                                fontSize: '14px',
                                padding: '4px 8px'
                            }}
                        >
                            {[10, 20, 50, 100].map(option => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="col-md-6 text-end">
                    <small style={{ color: 'var(--darkOrange)' }}>
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAssets.length)} of {filteredAssets.length} assets
                    </small>
                </div>
            </div>
            
            {filteredAssets.length === 0 ? (
                <div className="no-assets">
                    <p>{assets.length === 0 ? 'No assets uploaded yet.' : 'No assets found matching your search.'}</p>
                </div>
            ) : (
                <>
                    <div className="assets-grid">
                        {currentAssets.map(asset => (
                        <div key={asset._id} className="asset-card">
                            <div className="asset-header">
                                <h3>{asset.name}</h3>
                            </div>
                            
                            <div className="asset-content">
                                {/* Asset ID */}
                                <div className="asset-file">
                                    <strong>Asset ID:</strong>
                                    <span className="asset-id">{asset._id}</span>
                                </div>
                            </div>
                            
                            <div className="asset-actions">
                                <button 
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleEdit(asset._id)}
                                >
                                    Edit
                                </button>
                                <button 
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleDelete(asset._id, asset.name)}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                    </div>
                    
                    {/* Pagination Controls */}
                    {filteredAssets.length > 0 && (
                        <StandardPagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            itemsPerPage={itemsPerPage}
                            onItemsPerPageChange={setItemsPerPage}
                            totalItems={filteredAssets.length}
                            itemsPerPageLabel="Assets per page:"
                            pageInfoLabel="Showing"
                            showItemsPerPage={false}
                        />
                    )}
                </>
            )}

            {/* Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="modal-overlay">
                    <div className="delete-confirmation-modal">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this asset?</p>
                            <p className="asset-name">"{deleteModal.assetName}"</p>
                            <p className="warning-text">This action cannot be undone.</p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary"
                                onClick={cancelDelete}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger"
                                onClick={confirmDelete}
                                disabled={loading}
                            >
                                {loading ? 'Deleting...' : 'Delete Asset'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllAssets;
